import { Filter, HorseshoeConfig } from '../shared/types/podlet';
import {
    BannerGlobal,
    Finn,
    Keywords,
    PageType,
    Subvertical,
    Vertical,
    XandrData,
} from '../shared/types/ast';
import {
    markBannerRefresh,
    markFilterUpdated,
    markPageLoaded,
} from './utils/performance';
import { publish, subscribe } from './podlet/events';
import { verticalSpesificUpdate } from './podlet/vertical';
import { Feed, ReklameConfiguration } from '../shared/types/reklameMaster';
import { filterKeywordsLegacy } from '../shared/filter';
import { keyValuesEqual, nrmFeedToString } from '../shared/utils';
import { isEmpty } from '../shared/utils';
import { getSearchReklameConfig } from './services/reklame-master-provider/reklame-master-provider';
import { displayAdsLog } from './utils/debug';
import { preProcessTravelFilter } from '../shared/query-requests';
import { setPageOpts } from './podlet/ast';
import { setupPageOptsAndGetXandrData } from './utils/page-opts';
import { copyQueue } from './index';

class RefreshState {
    private _currentFilter: Filter | undefined;
    private _currentPageType: PageType | undefined;
    private _currentVertical: Vertical | undefined;
    private _currentFilterKeywords = {};
    private _currentSubvertical: Subvertical | undefined;
    private _keywords: Keywords;
    private _baseKeywords: Keywords;
    private _xandr: XandrData;
    private _clientReady = false;
    private _cycle = 0;

    get currentFilter(): Filter | undefined {
        return this._currentFilter;
    }

    set currentFilter(value: Filter | undefined) {
        this._currentFilter = value;
    }

    get currentFilterKeywords(): {} {
        return this._currentFilterKeywords;
    }

    set currentFilterKeywords(value: {}) {
        this._currentFilterKeywords = value;
    }

    get currentPageType(): PageType | undefined {
        return this._currentPageType;
    }

    set currentPageType(value: PageType | undefined) {
        this._currentPageType = value;
    }

    get currentVertical(): Vertical | undefined {
        return this._currentVertical;
    }

    set currentVertical(value: Vertical | undefined) {
        this._currentVertical = value;
    }
    get currentSubvertical(): Subvertical | undefined {
        return this._currentSubvertical;
    }

    set currentSubvertical(value: Subvertical | undefined) {
        this._currentSubvertical = value;
    }

    get xandr(): XandrData {
        return this._xandr;
    }

    set xandr(value: XandrData) {
        this._xandr = value;
    }

    get keywords(): Keywords {
        return this._keywords;
    }

    set keywords(value: Keywords) {
        this._keywords = value;
    }

    get baseKeywords(): Keywords {
        return this._baseKeywords;
    }

    set baseKeywords(value: Keywords) {
        this._baseKeywords = value;
    }

    get clientReady(): boolean {
        return this._clientReady;
    }

    set clientReady(value: boolean) {
        this._clientReady = value;
    }

    get cycle(): number {
        return this._cycle;
    }

    set cycle(value: number) {
        this._cycle = value;
    }

    /*
    Legacy handling of command based event
     */
    updateKeywords(filter: Filter) {
        const update = filterKeywordsLegacy(
            this.currentVertical,
            filter,
            this.currentFilter,
        );
        this.currentFilter = filter;

        if (update.keywords) {
            this.keywords = { ...this.keywords, ...update.keywords };
            if (window.apntag && window.apntag.anq) {
                window.apntag.anq.push(() => {
                    if (window.apntag) {
                        window.apntag.setPageOpts({
                            keywords: this.keywords,
                        });
                    }
                });
            }
        }

        if (update.refresh) {
            if (window.apntag && window.apntag.anq) {
                window.apntag.anq.push(() => {
                    if (window.apntag) {
                        window.apntag.refresh();
                    }
                });
            }
        }
    }

    pageChange(existingFilter?: Filter, updateFilter?: Filter) {
        if (updateFilter?.pageType) {
            return true;
        }
        return (
            existingFilter?.page !== updateFilter?.page ||
            existingFilter?.side !== updateFilter?.side
        );
    }

    shouldRefresh(vertical: string, keywordDiff = false) {
        switch (vertical) {
            case 'car':
            case 'bap':
                return keywordDiff;
            default:
                return true;
        }
    }

    updateFeed(nrmFeed: Feed) {
        const nrmFeedString = nrmFeedToString(nrmFeed);

        window.FINN = window.FINN || ({} as Finn);
        window.FINN.banner = window.FINN.banner || ({} as BannerGlobal);
        window.FINN.banner.feed = nrmFeed;
        window.FINN.banner.feedString = nrmFeedString;
    }

    async getSearchReklameConfigFromApi(
        filter: Filter,
        config: HorseshoeConfig,
    ): Promise<ReklameConfiguration | null> {
        const { baseUrl, deviceType } = config;

        if (isEmpty(filter)) {
            return null;
        } else {
            displayAdsLog(
                `getSearchReklameConfig() ${JSON.stringify(filter)} `,
            );
            return await getSearchReklameConfig(deviceType, filter, baseUrl);
        }
    }

    configUpdated(updatedConfig: Filter, config: HorseshoeConfig) {
        if (this.keywords) {
            // Some travel subverticals don't know who they are before the client is loaded
            if (config.vertical && updatedConfig.subvertical) {
                this.keywords['no-sno-finn-subsection'] = [
                    `${config.vertical}_${updatedConfig.subvertical}`,
                ];
                this.currentSubvertical =
                    updatedConfig.subvertical as Subvertical;
            }
            if (updatedConfig.pageType) {
                this.keywords['aa-sch-page_type'] = [
                    updatedConfig.pageType as string,
                ];
            }
        }
    }

    refreshBanners() {
        displayAdsLog(`refreshBanners()`);
        if (this.clientReady) {
            this.cycle = this.cycle + 1;
            markBannerRefresh();

            // publish to ad.ts
            publish('banner', 'refresh', this.cycle);
        } else {
            this.clientReady = true;
            copyQueue();
        }
    }

    getXandrRequestData() {
        return { ...this.xandr, ...this.xandr.pageOpts };
    }

    setInitialData(config: HorseshoeConfig) {
        if (config) {
            const { xandr, filter, pageType, subvertical, vertical } = config;
            this.currentPageType = pageType;
            this.currentSubvertical = subvertical;
            this.currentVertical = vertical;
            this.currentFilter = getFilterData(filter);
            this.baseKeywords = xandr?.pageOpts?.keywords || {};
            const pageOpts = setupPageOptsAndGetXandrData();
            this.keywords = pageOpts.keywords;

            this.xandr = {
                ...xandr,
                pageOpts,
            };
        }
    }

    setupSubscriptions(config) {
        // TODO: Is this important?
        subscribe('banner', 'update', (event) => {
            this.updateKeywords(event.payload as Filter);
        });

        subscribe('filter', 'updated', (event) => {
            markFilterUpdated();
            this.filterUpdated(event.payload as Filter, config);
        });

        /**
         * Used to set some keywords from published events on load, e.g. on travel:
         *  const payload = {
         *             pageType,
         *             side: page || 1,
         *             destinasjon: {
         *                 by: city || '',
         *                 land: country || '',
         *             },
         *         };
         */
        subscribe('page', 'loaded', (event) => {
            markPageLoaded();
            this.configUpdated(event.payload as Filter, config);
            this.filterUpdated(event.payload as Filter, config);
        });
    }

    /**
     * This is the only function called when a filter is updated
     * @param updatedFilter
     * @param config
     */
    async filterUpdated(updatedFilter: Filter, config: HorseshoeConfig) {
        const { vertical, subvertical } = config;

        verticalSpesificUpdate(config, vertical, this.currentSubvertical);

        displayAdsLog(
            `filter before verticalSpesificPreProccessing() ${JSON.stringify(
                updatedFilter,
            )}`,
        );
        const filter = preProcessTravelFilter(
            updatedFilter,
            vertical,
            subvertical,
        );
        displayAdsLog(
            `filter after verticalSpesificPreProccessing() ${JSON.stringify(
                filter,
            )}`,
        );

        const pageType = filter.pageType as PageType;

        // TODO: Is this important?
        if (pageType && pageType !== this.currentPageType) {
            filter['pagetype'] = [pageType];
            this.currentPageType = pageType;
        }

        const reklameConfig =
            subvertical === 'prepackage'
                ? ({} as ReklameConfiguration)
                : await this.getSearchReklameConfigFromApi(filter, config);

        const isPageChange = this.pageChange(this.currentFilter, filter);
        let refresh = isPageChange;
        let nrmFeed = window.FINN.banner.feed as Feed;

        if (reklameConfig) {
            const nrmKeywords = reklameConfig?.keywords || {};
            const keywordDiff = !keyValuesEqual(nrmKeywords, this.baseKeywords);
            const alwaysRefresh = this.shouldRefresh(vertical, keywordDiff);
            refresh = alwaysRefresh || isPageChange;

            this.currentFilter = filter;

            if (reklameConfig?.feed) {
                nrmFeed = reklameConfig?.feed;
            }

            const xandrPageOpts = this.xandr?.pageOpts || {};
            const xandrKeywords = xandrPageOpts?.keywords || {};

            this.keywords = {
                ...xandrKeywords,
                ...this.keywords,
                ...nrmKeywords,
            };
            this.xandr = {
                ...this.xandr,
                pageOpts: {
                    ...xandrPageOpts,
                    keywords: this.keywords,
                },
            };
        } else {
            displayAdsLog(`reklameConfig is undefined`);
        }

        if (refresh) {
            setPageOpts({ keywords: this.keywords }, () => {
                displayAdsLog(`this.refreshBanners`);
                this.refreshBanners();
            });
        }

        this.updateFeed(nrmFeed);
    }
}

function getFilterData(filterData: Filter) {
    if (filterData) {
        if (filterData.page) {
            filterData.page = Number(filterData.page);
        }
        if (filterData.side) {
            filterData.side = Number(filterData.side);
        }
    }
    return filterData;
}

let refreshState;

export function getRefreshState(): RefreshState {
    if (refreshState instanceof RefreshState) {
        return refreshState;
    } else {
        refreshState = new RefreshState();
        return refreshState;
    }
}
